export const uiModule = `# 通用模組：React + Vite + Tailwind 前台骨架

> 版本：v1.0
> 適用場景：任何需要 CSR（Client-Side Rendering）前台的 Cloudflare Pages 專案
> 輸入：頁面清單、路由結構、技術約束
> 輸出：可編譯的 Vite + React + Tailwind 專案骨架（含 placeholder 頁面）

---

## 1. 技術約束

| 項目 | 版本/規格 |
|---|---|
| React | 18.x |
| TypeScript | 5.x |
| Vite | 5.x |
| Tailwind CSS | 3.4.x |
| 路由 | React Router DOM 6.x |
| 圖標 | Lucide React |
| 動畫 | Framer Motion（可選）|
| 圖表 | Recharts（後台可選）|

---

## 2. 檔案結構（最小骨架）

\`\`\`
apps/web/
├── public/
│   └── _redirects            # SPA fallback
├── src/
│   ├── main.tsx              # React entry
│   ├── App.tsx               # Router + Context providers
│   ├── index.css             # Tailwind directives
│   ├── vite-env.d.ts
│   ├── types/                # 共用型別
│   │   └── index.ts
│   ├── context/              # React Context
│   │   └── AppContext.tsx
│   ├── hooks/                # Custom hooks
│   ├── pages/                # 頁面組件
│   ├── components/           # 共用組件
│   │   ├── layout/
│   │   │   ├── Header.tsx
│   │   │   └── Footer.tsx
│   │   └── common/
│   └── utils/
│       └── helpers.ts
├── .env                      # 本地環境變數
├── .env.example
├── package.json
├── tsconfig.json
├── tsconfig.app.json
├── tsconfig.node.json
├── tailwind.config.js
├── postcss.config.js
└── vite.config.ts
\`\`\`

---

## 3. 核心檔案模板

### vite.config.ts
\`\`\`ts
import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';

export default defineConfig({
  plugins: [react()],
  build: {
    outDir: 'dist',
    sourcemap: true,
  },
});
\`\`\`

### tailwind.config.js
\`\`\`js
/** @type {import('tailwindcss').Config} */
export default {
  content: [
    './index.html',
    './src/**/*.{js,ts,jsx,tsx}',
  ],
  theme: {
    extend: {},
  },
  plugins: [],
};
\`\`\`

### postcss.config.js
\`\`\`js
export default {
  plugins: {
    tailwindcss: {},
    autoprefixer: {},
  },
};
\`\`\`

### tsconfig.json
\`\`\`json
{
  "files": [],
  "references": [
    { "path": "./tsconfig.app.json" },
    { "path": "./tsconfig.node.json" }
  ]
}
\`\`\`

### tsconfig.app.json
\`\`\`json
{
  "compilerOptions": {
    "target": "ES2020",
    "useDefineForClassFields": true,
    "lib": ["ES2020", "DOM", "DOM.Iterable"],
    "module": "ESNext",
    "skipLibCheck": true,
    "moduleResolution": "bundler",
    "allowImportingTsExtensions": true,
    "resolveJsonModule": true,
    "isolatedModules": true,
    "noEmit": true,
    "jsx": "react-jsx",
    "strict": true,
    "noUnusedLocals": true,
    "noUnusedParameters": true,
    "noFallthroughCasesInSwitch": true,
    "baseUrl": ".",
    "paths": {
      "@/*": ["src/*"]
    }
  },
  "include": ["src"]
}
\`\`\`

### public/_redirects
\`\`\`
/* /index.html 200
\`\`\`

---

## 4. App.tsx 路由骨架

\`\`\`tsx
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import Layout from './components/layout/Layout';

// 頁面 placeholder（實際內容由 Layer 2 prompt 填充）
const Home = () => <div className="p-8">Home Placeholder</div>;
const About = () => <div className="p-8">About Placeholder</div>;
const NotFound = () => <div className="p-8">404</div>;

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
\`\`\`

---

## 5. Layout 骨架

\`\`\`tsx
// components/layout/Layout.tsx
import { ReactNode } from 'react';
import Header from './Header';
import Footer from './Footer';

export default function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
\`\`\`

### Header 骨架
\`\`\`tsx
import { Link } from 'react-router-dom';

export default function Header() {
  return (
    <header className="sticky top-0 bg-white shadow-sm z-50">
      <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link to="/" className="text-xl font-bold">{{BRAND_NAME}}</Link>
        <div className="hidden md:flex gap-6">
          <Link to="/" className="hover:text-blue-600">首頁</Link>
          <Link to="/about" className="hover:text-blue-600">關於</Link>
        </div>
      </nav>
    </header>
  );
}
\`\`\`

### Footer 骨架
\`\`\`tsx
export default function Footer() {
  return (
    <footer className="bg-gray-900 text-white py-8">
      <div className="container mx-auto px-4 text-center">
        <p>© {new Date().getFullYear()} {{BRAND_NAME}}. All rights reserved.</p>
      </div>
    </footer>
  );
}
\`\`\`

---

## 6. Context 骨架

\`\`\`tsx
// context/AppContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AppData {
  [key: string]: any;
}

interface AppContextType {
  data: AppData;
  loading: boolean;
  error: string | null;
}

const AppContext = createContext<AppContextType>({
  data: {},
  loading: true,
  error: null,
});

export function AppProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<AppData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(\`\${import.meta.env.VITE_API_URL}/api/data\`)
      .then(r => r.json())
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppContext.Provider value={{ data, loading, error }}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppData = () => useContext(AppContext);
\`\`\`

---

## 7. package.json 依賴

\`\`\`json
{
  "name": "{{PROJECT_NAME}}-web",
  "private": true,
  "version": "1.0.0",
  "type": "module",
  "scripts": {
    "dev": "vite",
    "build": "tsc && vite build",
    "preview": "vite preview",
    "deploy": "wrangler pages deploy dist --project-name={{PAGES_PROJECT_NAME}}"
  },
  "dependencies": {
    "react": "^18.3.1",
    "react-dom": "^18.3.1",
    "react-router-dom": "^6.25.1",
    "lucide-react": "^0.400.0",
    "framer-motion": "^11.3.0"
  },
  "devDependencies": {
    "@types/react": "^18.3.3",
    "@types/react-dom": "^18.3.0",
    "@vitejs/plugin-react": "^4.3.1",
    "autoprefixer": "^10.4.19",
    "postcss": "^8.4.39",
    "tailwindcss": "^3.4.6",
    "typescript": "^5.5.3",
    "vite": "^5.3.4",
    "wrangler": "^3.65.0"
  }
}
\`\`\`

---

## 8. 通用陷阱

1. **BrowserRouter 在 Pages 上**：Cloudflare Pages 是靜態託管，\`BrowserRouter\` 需要 \`_redirects\` 檔案
2. **環境變數前綴**：Vite 只暴露 \`VITE_\` 開頭的環境變數給前端
3. **TypeScript strict mode**：建議開啟，但注意 \`noUnusedLocals\` 會導致 placeholder 報錯
4. **Tailwind content 路徑**：確保包含 \`index.html\` 和 \`src/**/*\`
5. **wrangler deploy 與 build 分離**：\`npm run build\` 輸出 \`dist/\`，\`npm run deploy\` 上傳 \`dist/\`
`;
